import React, {
  useMemo,
  memo,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react'
import PresentationContext from './presentation-context'
import { generateSizeFn } from './utils'
import keyBy from 'lodash/keyBy'
import slideTypes from './slide-types/slide-types'
import withWindowSize from 'pobble-components/dist/with-window-size'

const Presentation = memo(
  ({
    data,
    readOnly,
    builder,
    slides,
    activeSlideId,
    slideProps,
    children,
    multiSlides,
    slideHolderClassName,
    slideContainerClassName,
    onSlideUpdate,
    thumbnail,
    windowSize,
    containerRef,
    printMode,
    className,
    slideWidth
  }) => {
    if (!data) return null

    const [slidesState, setSlidesState] = useState({})
    const [wrapperWidth, setWrapperWidth] = useState(null)
    const wrapperRef = useRef(null)

    const size = useCallback(
      n => {
        if (wrapperWidth === windowSize.width) {
          return `${n}vw`
        } else {
          return `${(wrapperWidth / 100) * n}px`
        }
      },
      [wrapperWidth, windowSize]
    )

    const setSlideState = useCallback(
      (id, state) => {
        setSlidesState({ ...slidesState, [id]: state })
      },
      [slidesState]
    )

    useEffect(() => {
      setWrapperWidth(
        slideWidth
          ? slideWidth
          : containerRef
          ? containerRef.current.offsetWidth
          : wrapperRef.current.offsetWidth
      )
    }, [windowSize.width])

    const {
      accessibility_color,
      font_size,
      year_groups,
      all_year_groups,
      image,
      pattern,
      asset,
      show_age,
      font_type,
      audio_url
    } = data

    const autoGeneratedSlides = useMemo(() => {
      if (slides) return null
      return keyBy(data.slides, 'id')
    }, [data, slides])

    slides = slides || autoGeneratedSlides

    const slideData =
      data.asset && !builder
        ? data.slides[data.slides.length - 1]
        : slides[activeSlideId]

    const adaptiveSize = useMemo(
      () => (n, options) =>
        generateSizeFn(n, options, { fontSize: font_size }, size),
      [size, font_size]
    )

    const currentSlide = useMemo(() => {
      if (multiSlides) return null
      const Slide = slideTypes[slideData.type]
      return <Slide key={slideData.id} id={slideData.id} />
    }, [slideData, multiSlides])

    const slidesList = useMemo(() => {
      if (!multiSlides) return null

      // Return only one slide for asset presentation
      if (data.asset) {
        const slideData = data.slides[data.slides.length - 1]
        const Slide = slideTypes[slideData.type]
        return [<Slide key={slideData.id} id={slideData.id} />]
      } else {
        return data.slides.map(slide => {
          const slideData = slides[slide.id]
          const Slide = slideTypes[slideData.type]
          return <Slide key={slideData.id} id={slideData.id} />
        })
      }
    }, [data, slides, multiSlides])

    const providerValue = useMemo(() => {
      return {
        size: adaptiveSize,
        audio_url: audio_url,
        accessibilityColor: accessibility_color,
        fontSize: font_size,
        slides,
        readOnly,
        builder,
        slideProps,
        yearGroups: year_groups,
        allYearGroups: all_year_groups,
        image,
        setSlideState,
        slidesState,
        onSlideUpdate,
        presentationId: data.id,
        pattern: pattern,
        slideHolderClassName,
        slideContainerClassName,
        thumbnail,
        asset,
        printMode,
        showAge: show_age,
        fontType: font_type
      }
    }, [
      adaptiveSize,
      accessibility_color,
      font_size,
      readOnly,
      builder,
      slideProps,
      year_groups,
      all_year_groups,
      image,
      setSlideState,
      slidesState,
      onSlideUpdate,
      data,
      pattern,
      slides,
      slideHolderClassName,
      slideContainerClassName,
      thumbnail,
      asset,
      printMode,
      show_age,
      font_type,
      audio_url
    ])

    return (
      <PresentationContext.Provider value={providerValue}>
        {children ? (
          children({ currentSlide, slideData, slides: slidesList })
        ) : (
          <div ref={wrapperRef} className={className}>
            {wrapperWidth && currentSlide}
          </div>
        )}
      </PresentationContext.Provider>
    )
  }
)

export default withWindowSize(Presentation)
